import { takeEvery, takeLatest, delay } from 'redux-saga'
import { call, put, take, fork, select, race, cancel, all } from 'redux-saga/effects'

import actionTypes from 'redux/actions/actionTypes'
import { quizSelectors } from 'redux/selectors'
import { quizSync, iconSync, slideSync } from 'sync'
import fileUploader from 'helpers/fileUploader'
import callAny from './helpers/call_any'
import formatError from './helpers/error_formatter'
import { localize } from 'helpers/translator'
import errorCodes from 'helpers/enums/errorCodes'
import iconTypes from 'helpers/enums/iconTypes'
import quizActions from 'redux/actions/quizActions'

function *quizCreateInitialize(action) {
	try {
		const response = yield callAny(quizSync.getQuiz, action.quizUID, action.jwt)
		yield put({
			type: actionTypes.QUIZ_INITIALIZE_SUCCESS,
			data: response.payload.quiz,
		})
	} catch (error) {
		yield put({
			type: actionTypes.QUIZ_INITIALIZE_ERROR,
		})
	}
}

function *quizCreateSaveQuiz() {
	const successActions = [
		actionTypes.QUIZ_QUESTION_UPDATE_SUCCESS,
		actionTypes.QUIZ_DELETE_QUESTION_SUCCESS,
		actionTypes.QUIZ_DROP_QUESTION_SUCCESS,
		actionTypes.QUIZ_TITLE_SAVE_SUCCESS,
		actionTypes.QUIZ_QUESTION_SAVE_SUCCESS,
		actionTypes.QUIZ_UPLOADER_UPLOAD_SUCCESS,
	]
	const errorActions = [
		actionTypes.QUIZ_UPLOAD_REFERENCE_MEDIA_ERROR,
		actionTypes.QUIZ_UPLOADER_VALIDATE_ERROR,
		actionTypes.QUIZ_UPLOADER_UPLOAD_ERROR,
		actionTypes.PROCESSING_ICON_ERROR, //@todo: hmike: validate if icon belongs to Quiz
		actionTypes.QUIZ_UPLOADER_VALIDATE_CONTENT_ERROR,
		actionTypes.QUIZ_QUESTION_UPDATE_ERROR,
	]
	let saveSelector

	try {
		const validationSelector = yield select(quizSelectors.validationSelector)
		if (validationSelector.valid) {
			saveSelector = yield select(quizSelectors.saveSelector)
			yield put({
				type: actionTypes.QUIZ_VALIDATION_SUCCESS,
				text: saveSelector.allIconsDone ? null : localize('quiz_will_save_after_uploading'),
			})
			let allRequestsDone = false

			// Checks if there is any question that has changed and was not saved
			// Then for each one it dispatches a new action to save the question
			const questionsChanged = yield select(quizSelectors.questionsChangedSelector)
			yield all(questionsChanged.map(question => put(quizActions.questionSave(question.id))))

			while (!allRequestsDone) {
				saveSelector = yield select(quizSelectors.saveSelector)

				allRequestsDone = saveSelector.allQuestionsDone && saveSelector.allIconsDone

				// Only send quiz save success actions if all questions and icons were saved
				if (allRequestsDone) {
					const response = yield callAny(quizSync.saveQuiz, saveSelector.entityUID)
					yield put({
						type: actionTypes.QUIZ_SAVE_QUIZ_SUCCESS,
						redirectUrl: response.payload.redirectUrl,
						metrics: response.payload.metrics,
					})
				} else {
					const requestActions = yield take([...successActions, ...errorActions])
					if (errorActions.includes(requestActions.type)) {
						allRequestsDone = true
						throw requestActions.error || new Error('Save Undefined Error')
					}
				}
			}

		} else {
			yield put({
				type: actionTypes.QUIZ_VALIDATION_ERROR,
				message: validationSelector.message,
			})
		}
	} catch (error) {
		yield put(quizActions.saveQuizError(formatError(error)))
	}
}

function *quizCreateDeleteQuestionValidation(action) {
	try {
		const deleteQuestionValidationSelector = yield select(quizSelectors.deleteQuestionValidationSelector)
		if (deleteQuestionValidationSelector.valid) {
			yield put({
				type: actionTypes.QUIZ_DELETE_QUESTION_VALIDATION_SUCCESS,
				questionId: action.questionId,
				serverId: action.serverId,
			})
		} else {
			yield put({
				type: actionTypes.QUIZ_DELETE_QUESTION_VALIDATION_ERROR,
				message: deleteQuestionValidationSelector.message,
			})
		}
	} catch (error) {
		// console.log(error)
	}
}

function *quizCreateDeleteQuestion(action) {
	try {
		const questionSelector = yield call(waitForQuestionCreation, action.questionId)
		yield callAny(quizSync.deleteQuestion, questionSelector.serverId)
		yield put({
			type: actionTypes.QUIZ_DELETE_QUESTION_SUCCESS,
			questionId: action.questionId,
		})
	} catch (error) {
		yield put({
			type: actionTypes.QUIZ_DELETE_QUESTION_ERROR,
			questionId: action.questionId,
		})
	}
}

function *quizCreateDeleteAnswerValidation(action) {
	try {
		const deleteAnswerValidationSelector = yield select(quizSelectors.deleteAnswerValidationSelector(action.questionId))
		if (deleteAnswerValidationSelector.valid) {
			yield put({
				type: actionTypes.QUIZ_DELETE_ANSWER_VALIDATION_SUCCESS,
				questionId: action.questionId,
				answerId: action.answerId,
			})
		} else {
			yield put({
				type: actionTypes.QUIZ_DELETE_ANSWER_VALIDATION_ERROR,
				message: deleteAnswerValidationSelector.message,
			})
		}
	} catch (error) {
		// console.log(error)
	}
}

function *quizCreateTimerSave() {
	try {
		const { entityUID, timerValue } = yield select(quizSelectors.saveSelector)
		yield callAny(slideSync.saveTimer, timerValue, entityUID)
		yield put({
			type: actionTypes.QUIZ_TIMER_SAVE_SUCCESS,
		})
	} catch (error) {
		yield put({
			type: actionTypes.QUIZ_TIMER_SAVE_ERROR,
		})
	}
}

function *quizCreateTitleSave() {
	try {
		const { entityUID, instructionsValue } = yield select(quizSelectors.saveSelector)
		yield callAny(quizSync.saveTitle, instructionsValue, entityUID)
		yield put({
			type: actionTypes.QUIZ_TITLE_SAVE_SUCCESS,
		})
	} catch (error) {
		yield put({
			type: actionTypes.QUIZ_TITLE_SAVE_ERROR,
		})
	}
}

function *quizCreateAddAnswerValidation(action) {
	try {
		const addAnswerValidationSelector = yield select(quizSelectors.addAnswerValidationSelector(action.questionId))
		if (addAnswerValidationSelector.valid) {
			yield put({
				type: actionTypes.QUIZ_ADD_ANSWER_VALIDATION_SUCCESS,
				questionId: action.questionId,
			})
		} else {
			yield put({
				type: actionTypes.QUIZ_ADD_ANSWER_VALIDATION_ERROR,
				message: addAnswerValidationSelector.message,
			})
		}
	} catch (error) {
		// console.log(error)
	}
}

function *quizCreateUpdateQuestion(action) {
	try {
		yield call(delay, 2000)
		const questionSelector = yield call(waitForQuestionCreation, action.questionId)
		yield callAny(quizSync.updateQuestion, questionSelector.text, questionSelector.serverId, questionSelector.icon.id, questionSelector.answers)
		yield put({
			type: actionTypes.QUIZ_QUESTION_UPDATE_SUCCESS,
			questionId: action.questionId,
		})
	} catch (error) {
		yield put(quizActions.questionUpdateError(action.questionId, error))
	}
}

function *waitForQuestionCreation(questionId) {
	let hasQuestionId = false
	let questionSelector
	while (!hasQuestionId) {
		questionSelector = yield select(quizSelectors.questionSelector(questionId))
		if (!questionSelector.serverId) {
			const questionActions = yield take([actionTypes.QUIZ_QUESTION_SAVE_SUCCESS,
				actionTypes.QUIZ_QUESTION_SAVE_ERROR,
				actionTypes.QUIZ_DELETE_QUESTION_SUCCESS])
			if (questionActions.questionId === questionId && questionActions.type !== actionTypes.QUIZ_QUESTION_SAVE_SUCCESS) {
				throw new Error()
			}
		} else {
			hasQuestionId = true
		}
	}
	return questionSelector
}

function *quizUploadReferenceMedia(action) {
	try {
		let questionSelector = yield select(quizSelectors.questionSelector(action.questionId))
		const validateResponse = yield callAny(iconSync.validateFilesPicked, action.allowedTypes, action.files['files'], questionSelector.presentationId, questionSelector.entityUID, action.force)
		const payload = validateResponse.payload.icons[0]
		const file = action.files['files'][0]
		if (payload.error_code === 0) {
			yield put(quizActions.uploaderValidateSuccess(action.questionId, payload.uploadId))
			questionSelector = yield call(waitForQuestionCreation, action.questionId)
			const {uploadResponse} = yield race({
				questionOrIconDeleted: call(takeQuestionOrIconDeletionWithId, action.questionId),
				uploadResponse: callAny(fileUploader, payload.uploadId, file, 'Question', questionSelector.serverId, iconTypes.ENTITY_ICON_TYPE_REFERENCE_MEDIA, action.allowedTypes, questionSelector.presentationId, questionSelector.entityUID, action.force),
			})
			if (uploadResponse) {
				yield put({
					type: actionTypes.QUIZ_UPLOADER_UPLOAD_SUCCESS,
					questionId: action.questionId,
					icon: uploadResponse.payload.icon,
				})
			}
		} else {
			// webpage content validation
			if (payload.error_code === errorCodes.ICON_INVALID_CONTENT_INTEGRITY) {
				yield put({
					type: actionTypes.QUIZ_UPLOADER_VALIDATE_CONTENT_ERROR,
					message: payload.message,
					questionId: action.questionId,
				})
				const uploadConfirmationAction = yield take(actionTypes.QUIZ_UPLOAD_REQUIRE_CONFIRMATION)
				if (uploadConfirmationAction.questionId === action.questionId) {
					yield put({
						type: actionTypes.QUIZ_UPLOAD_REFERENCE_MEDIA,
						allowedTypes: action.allowedTypes,
						files: action.files,
						featureType: action.featureType,
						questionId: action.questionId,
						force: true,
					})
				}
			} else {
				yield put({
					type: actionTypes.QUIZ_UPLOADER_VALIDATE_ERROR,
					message: payload.message,
					questionId: action.questionId,
				})
			}
		}
	} catch (error) {
		let message = localize('quiz_service_error_message')
		if (error.serverError.message) {
			message = error.serverError.message
		}
		yield put({
			type: actionTypes.QUIZ_UPLOADER_UPLOAD_ERROR,
			error: error.serverError,
			questionId: action.questionId,
			message,
		})
	}
}

function *quizCreateDropQuestion() {
	try {
		const dropQuestionSelector = yield select(quizSelectors.dropQuestionSelector)
		yield callAny(quizSync.dropQuestion, dropQuestionSelector.entityUID, dropQuestionSelector.questions)
		yield put({
			type: actionTypes.QUIZ_DROP_QUESTION_SUCCESS,
		})
	} catch (error) {
		yield put({
			type: actionTypes.QUIZ_DROP_QUESTION_ERROR,
		})
	}
}

function *quizCreateAddQuestionValidation(action) {
	try {
		const addQuestionValidationSelector = yield select(quizSelectors.addQuestionValidationSelector)
		if (addQuestionValidationSelector.valid) {
			yield put({
				type: actionTypes.QUIZ_ADD_QUESTION_VALIDATION_SUCCESS,
				questionId: action.questionId,
			})
		} else {
			yield put({
				type: actionTypes.QUIZ_ADD_QUESTION_VALIDATION_ERROR,
				message: addQuestionValidationSelector.message,
			})
		}
	} catch (error) {
		// console.log(error)
	}
}

function *quizCreateAddQuestion(action) {
	try {
		const saveSelector = yield select(quizSelectors.saveSelector)
		const response = yield callAny(quizSync.addQuestion, saveSelector.entityUID)
		yield put({
			type: actionTypes.QUIZ_QUESTION_SAVE_SUCCESS,
			questionId: action.questionId,
			serverId: response.payload.id,
		})
	} catch (error) {
		yield put({
			type: actionTypes.QUIZ_QUESTION_SAVE_ERROR,
			questionId: action.questionId,
			error: error.serverError,
		})
	}
}

function *quizCreateDuplicateQuestionValidation(action) {
	try {
		const addQuestionValidationSelector = yield select(quizSelectors.addQuestionValidationSelector)
		if (addQuestionValidationSelector.valid) {
			yield put({
				type: actionTypes.QUIZ_DUPLICATE_QUESTION_VALIDATION_SUCCESS,
				questionId: action.questionId,
				duplicateQuestionId: action.duplicateQuestionId,
			})
		} else {
			yield put({
				type: actionTypes.QUIZ_ADD_QUESTION_VALIDATION_ERROR,
				message: addQuestionValidationSelector.message,
			})
		}
	} catch (error) {
		// console.log(error)
	}
}

function *quizCreateDuplicateQuestion(action) {
	try {
		const questionSelector = yield select(quizSelectors.questionSelector(action.duplicateQuestionId))
		const response = yield callAny(quizSync.duplicateQuestion, questionSelector.entityUID, questionSelector.presentationId, questionSelector.text, questionSelector.icon.id, questionSelector.answers, questionSelector.orden)
		yield put({
			type: actionTypes.QUIZ_QUESTION_SAVE_SUCCESS,
			questionId: action.questionId,
			serverId: response.payload.id,
		})
	} catch (error) {
		yield put({
			type: actionTypes.QUIZ_QUESTION_SAVE_ERROR,
			error: error.serverError,
			questionId: action.questionId,
			message: error.serverError.message,
		})
	}
}

function *takeQuestionOrIconDeletionWithId(questionId) {
	let isDeleted = false
	while (!isDeleted) {
		const action = yield take([actionTypes.QUIZ_DELETE_QUESTION_SUCCESS, actionTypes.QUIZ_DELETE_REFERENCE_MEDIA])
		if (action.questionId === questionId) {
			isDeleted = true
		}
	}
}

function *quizDeleteReferenceMedia(action) {
	try {
		const questionSelector = yield select(quizSelectors.questionSelector(action.questionId))
		yield put(quizActions.deleteReferenceMediaSuccess(action.questionId))
		if (questionSelector.icon.status === 'uploading') {
			yield callAny(iconSync.cancelUpload, questionSelector.icon.uploadId)
		}

	} catch (error) {
		// console.log(error)
	}
}

function *quizTakeLatestCreateUpdateQuestion() {
	//this is a customized take latest for updating questions
	//to avoid server concurrency we use the delay
	//and to avoid missing updates for different questions, we compare the last one with the previous one
	let questionId = 0
	let previousFork = null
	let action
	while (true) {
		action = yield take([actionTypes.QUIZ_QUESTION_SAVE,
			actionTypes.QUIZ_ANSWER_SAVE,
			actionTypes.QUIZ_DELETE_ANSWER_VALIDATION_SUCCESS,
			actionTypes.QUIZ_DROP_ANSWER,
			actionTypes.QUIZ_ADD_ANSWER_VALIDATION_SUCCESS,
			actionTypes.QUIZ_ANSWER_CHECKBOX_CHANGE])
		if (questionId === action.questionId && previousFork !== null) {
			yield cancel(previousFork)
		}
		questionId = action.questionId
		previousFork = yield fork(quizCreateUpdateQuestion, action)
	}
}

function *quizCancelChanges() {
	const cancelSelector = yield select(quizSelectors.quizCancelSelector)
	if (cancelSelector.isModified) {
		yield put({
			type: actionTypes.QUIZ_CANCEL_QUIZ_CONFIRM,
		})
	} else {
		yield put({
			type: actionTypes.QUIZ_CANCEL_QUIZ_CONFIRMED,
		})
	}
}


function *quizHtmlToPlainText({questionId}) {
	yield call(delay, 1000)
	yield put(quizActions.questionToPlainText(questionId))
}

export default [
	takeLatest([
		actionTypes.QUIZ_INITIALIZE,
		actionTypes.QUIZ_SCREENSHOT_INITIALIZE_ROUTE,
		actionTypes.QUIZ_INITIALIZE_STANDALONE,
	], quizCreateInitialize),
	takeLatest(actionTypes.QUIZ_SAVE_QUIZ, quizCreateSaveQuiz),
	takeLatest(actionTypes.QUIZ_TITLE_SAVE, quizCreateTitleSave),
	takeLatest([
		actionTypes.QUIZ_SAVE_TIMER,
		actionTypes.QUIZ_DELETE_TIMER,
	], quizCreateTimerSave),
	takeLatest(actionTypes.QUIZ_DELETE_QUESTION, quizCreateDeleteQuestionValidation),
	takeEvery(actionTypes.QUIZ_DELETE_QUESTION_VALIDATION_SUCCESS, quizCreateDeleteQuestion),
	takeLatest(actionTypes.QUIZ_DELETE_ANSWER, quizCreateDeleteAnswerValidation),
	takeEvery(actionTypes.QUIZ_UPLOAD_REFERENCE_MEDIA, quizUploadReferenceMedia),
	takeLatest(actionTypes.QUIZ_DROP_QUESTION, quizCreateDropQuestion),
	takeEvery(actionTypes.QUIZ_ADD_QUESTION_VALIDATION_SUCCESS, quizCreateAddQuestion),
	takeEvery(actionTypes.QUIZ_DUPLICATE_QUESTION_VALIDATION_SUCCESS, quizCreateDuplicateQuestion),
	fork(quizTakeLatestCreateUpdateQuestion),
	takeLatest(actionTypes.QUIZ_ADD_QUESTION, quizCreateAddQuestionValidation),
	takeLatest(actionTypes.QUIZ_DUPLICATE_QUESTION, quizCreateDuplicateQuestionValidation),
	takeLatest(actionTypes.QUIZ_ADD_ANSWER, quizCreateAddAnswerValidation),
	takeLatest(actionTypes.QUIZ_DELETE_REFERENCE_MEDIA, quizDeleteReferenceMedia),
	takeEvery(actionTypes.QUIZ_DELETE_REFERENCE_MEDIA_SUCCESS, quizCreateUpdateQuestion),
	takeLatest(actionTypes.QUIZ_CANCEL_QUIZ, quizCancelChanges),
	takeLatest(actionTypes.QUIZ_QUESTION_CHANGE, quizHtmlToPlainText),
]
